import { ARC, polarToCartesian } from './dialGeometry';

import type { ReactNode } from 'react';

import { useSvgId } from '@/hooks';

/**
 * The cap that closes the arc: a soft halo, a pale face, and a ring in the brand colour. The three
 * radii are read from the reference drawing at this ring radius, and stated as shares of the band
 * so they follow it if the dial is ever resized again.
 */
const ARC_END_CAP = {
    halo: ARC.stroke * 1.25,
    face: ARC.stroke * 0.73,
    ring: ARC.stroke * 0.345,
};

/**
 * Width of the translucent ring the halo shows around the picture. The reference drawing gets the
 * same effect by pushing the photograph twenty pixels below the ring's centre, which reads as a
 * misalignment rather than as a halo: a ring of even width says the same thing and stays centred.
 */
const HALO_RING = 5;

/** Past half a turn, an SVG arc needs its large-arc flag. */
const HALF_TURN = 180;

const CENTRE = ARC.size / 2;

/**
 * The picture's share of the dial's box. Everything inside scales together on a narrow card:
 * the drawing follows its `viewBox`, and the picture follows this ratio rather than a pixel
 * count that would stay put while the ring shrank around it.
 */
const PICTURE_SHARE = `${(((ARC.centreDiameter - HALO_RING * 2) / ARC.size) * 100).toFixed(3)}%`;

interface ScoreArcProps {
    /** How much of the scale is already covered, 0 to 100. */
    percentage: number;
    /** What sits at the centre of the dial -- the employee's picture. */
    children?: ReactNode;
}

/**
 * The dial's ring, and the disc it encircles: the track it always draws, the gradient arc and its
 * cap once there is progress to show, and the translucent halo the picture sits on.
 *
 * It knows nothing of the score -- how far the arc sweeps is handed to it as a share of the scale,
 * so what the tile says and how the dial is drawn can change without disturbing each other. Every
 * size comes from `ARC`: the disc is derived from the ring, never set beside it.
 */
export const ScoreArc = ({ percentage, children }: ScoreArcProps) => {
    const arcGradientId = useSvgId('performance-score-arc');
    const filled = Math.min(Math.max(percentage, 0), 100);
    const sweep = (filled / 100) * ARC.sweep;

    const trackStart = polarToCartesian(ARC.startAngle);
    const trackEnd = polarToCartesian(ARC.startAngle + ARC.sweep);
    const arcEnd = polarToCartesian(ARC.startAngle + sweep);

    const trackPath = `M ${trackStart.x} ${trackStart.y} A ${ARC.radius} ${ARC.radius} 0 1 1 ${trackEnd.x} ${trackEnd.y}`;
    const largeArcFlag = sweep > HALF_TURN ? 1 : 0;
    const arcPath = `M ${trackStart.x} ${trackStart.y} A ${ARC.radius} ${ARC.radius} 0 ${largeArcFlag} 1 ${arcEnd.x} ${arcEnd.y}`;

    return (
        <div
            className={'relative flex w-full items-center justify-center'}
            style={{ maxWidth: ARC.size, aspectRatio: '1' }}
        >
            <svg
                width={'100%'}
                height={'100%'}
                viewBox={`0 0 ${ARC.size} ${ARC.size}`}
                fill={'none'}
                aria-hidden={'true'}
            >
                <defs>
                    {/* The reference drawing runs its gradient from the upper right down to the
                        lower left, against the arc's own direction: the turquoise end meets the
                        start of the sweep and the orchid end its finish. */}
                    <linearGradient
                        id={arcGradientId}
                        x1={'100%'}
                        y1={'0%'}
                        x2={'0%'}
                        y2={'100%'}
                    >
                        <stop style={{ stopColor: 'var(--color-growth-arc-start)' }} />
                        <stop
                            offset={'1'}
                            style={{ stopColor: 'var(--color-growth-arc-end)' }}
                        />
                    </linearGradient>
                </defs>

                {/* The disc the picture sits on: translucent white behind a white hairline, so the
                    photograph reads as inset in the dial rather than laid over it. */}
                <circle
                    cx={CENTRE}
                    cy={CENTRE}
                    r={ARC.centreDiameter / 2}
                    className={'fill-white/60 stroke-white'}
                />

                <path
                    d={trackPath}
                    className={'stroke-growth-violet-tint'}
                    strokeWidth={ARC.stroke}
                    strokeLinecap={'round'}
                />

                {filled > 0 && (
                    <>
                        <path
                            d={arcPath}
                            stroke={`url(#${arcGradientId})`}
                            strokeWidth={ARC.stroke}
                            strokeLinecap={'round'}
                        />
                        <circle
                            cx={arcEnd.x}
                            cy={arcEnd.y}
                            r={ARC_END_CAP.halo}
                            className={'fill-growth-arc-cap-halo'}
                        />
                        <circle
                            cx={arcEnd.x}
                            cy={arcEnd.y}
                            r={ARC_END_CAP.face}
                            className={'fill-growth-arc-cap-face stroke-primary'}
                            strokeWidth={ARC_END_CAP.ring}
                        />
                    </>
                )}
            </svg>

            <div
                className={'absolute overflow-hidden rounded-full'}
                style={{ width: PICTURE_SHARE, height: PICTURE_SHARE }}
            >
                {children}
            </div>
        </div>
    );
};
